import { z } from "zod";

/**
 * Wire-shape mirror of the shore's settlement worksheet (PAYD-FR-030 to
 * FR-034, FR-110, FR-113): `SettlementDto`, `FnfLineDto`, the `SettlementRule`
 * row the worksheet carries, and the request bodies `settlement.controller.ts`
 * takes.
 *
 * Money is a string in both directions, the same reason every other payroll
 * schema gives: `Decimal(15, 2)` on the shore, and parsing it here would undo
 * the precision the field exists to protect.
 *
 * The rule row is mirrored in full rather than summarised. A settlement is the
 * last thing a company does to a person, and the two facts this row decides
 * (whether they are charged for notice, whether five years of service are
 * waived) are facts the screen has to be able to STATE, not imply.
 */

/// `settlement-rules.ts` SETTLEMENT_EXIT_REASONS, copied.
export const SETTLEMENT_EXIT_REASONS = [
  "resignation",
  "termination_by_employer",
  "retrenchment",
  "retirement",
  "death",
  "disablement",
  "absconding",
  "end_of_contract",
  "mutual",
] as const;
export type SettlementExitReason = (typeof SETTLEMENT_EXIT_REASONS)[number];

/// `settlement-rules.ts` SETTLEMENT_MODES, copied.
export const SETTLEMENT_MODES = ["on_date", "with_regular_cycle"] as const;
export type SettlementMode = (typeof SETTLEMENT_MODES)[number];

/// `settlement-rules.ts` SETTLEMENT_STATUSES, copied.
export const SETTLEMENT_STATUSES = ["draft", "finalized", "settled", "cancelled"] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

/// `settlement-rules.ts` FNF_LINE_KINDS, copied.
export const FNF_LINE_KINDS = [
  "gratuity",
  "leave_encashment",
  "expense_claim",
  "advance",
  "withheld_salary",
  "bonus",
  "asset_recovery",
  "other",
  "notice_recovery",
  "notice_pay",
  "bonus_recovery",
  "salary_overpayment_recovery",
  "leave_advance_recovery",
  "salary_to_lwd",
] as const;
export type FnfLineKind = (typeof FNF_LINE_KINDS)[number];

export const FNF_LINE_SIDES = ["payable", "receivable"] as const;
export type FnfLineSide = (typeof FNF_LINE_SIDES)[number];

export const settlementRuleSchema = z
  .object({
    exitReason: z.string(),
    noticeDirection: z.string(),
    gratuityWaived: z.boolean(),
    terminalExemptionKind: z.string().nullable(),
    alumniDefault: z.boolean(),
    notificationPolicy: z.string(),
    requiresConfirmedLastWorkingDay: z.boolean(),
    dueDateWorkingDays: z.number(),
  })
  .strict();

export type SettlementRule = z.infer<typeof settlementRuleSchema>;

export const fnfLineSchema = z
  .object({
    id: z.string(),
    side: z.string(),
    kind: z.string(),
    description: z.string(),
    amount: z.string(),
    currency: z.string(),
    status: z.string(),
    refType: z.string().nullable(),
    refId: z.string().nullable(),
    sourceRef: z.string().nullable(),
    waivedAmount: z.string().nullable(),
    waiveReason: z.string().nullable(),
    version: z.number(),
  })
  .strict();

export type FnfLine = z.infer<typeof fnfLineSchema>;

export const payeeOverrideReadSchema = z
  .object({
    name: z.string(),
    relationship: z.string(),
    bank: z.object({
      accountHolderName: z.string(),
      ifscCode: z.string(),
      accountNumberLast4: z.string(),
    }),
    proofAttachmentId: z.string().nullable(),
    /// True when stored JSON is present but does not match the write shape.
    needsReview: z.boolean(),
  })
  .strict();

export type PayeeOverrideRead = z.infer<typeof payeeOverrideReadSchema>;

export const payeeOverrideWriteSchema = z
  .object({
    name: z.string(),
    relationship: z.string(),
    bank: z.object({
      accountHolderName: z.string(),
      accountNumber: z.string(),
      ifscCode: z.string(),
    }),
    proofAttachmentId: z.string().optional(),
  })
  .strict();

export type PayeeOverrideWrite = z.infer<typeof payeeOverrideWriteSchema>;

export const settlementSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    /// Who the settlement is about, straight off the wire. Mirrored rather
    /// than resolved here because there is nothing on this side to resolve it
    /// against: an exit soft-deletes the membership, so the salary overview
    /// roster and every other live member list the webui can reach exclude the
    /// subject of every row on this list. Joining against one is what made the
    /// list render "Member" and the worksheet header "This leaver".
    memberName: z.string(),
    relievingDate: z.string(),
    exitReason: z.string(),
    rule: settlementRuleSchema,
    status: z.string(),
    settlementMode: z.string(),
    settlementDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    currency: z.string(),
    totalPayable: z.string(),
    totalReceivable: z.string(),
    netAmount: z.string(),
    personalEmail: z.string().nullable(),
    /**
     * Nominee or legal-heir payee when HR overrode the leaver's own bank
     * details. Null when the settlement pays the leaver. Account number is
     * last four only.
     */
    payeeOverride: payeeOverrideReadSchema.nullable(),
    settlementRunId: z.string().nullable(),
    settlementSlipId: z.string().nullable(),
    cancelReason: z.string().nullable(),
    version: z.number(),
    lines: z.array(fnfLineSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export type Settlement = z.infer<typeof settlementSchema>;

export const settlementsPageSchema = z
  .object({
    rows: z.array(settlementSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export type SettlementsPage = z.infer<typeof settlementsPageSchema>;

/// Request bodies. `OpenSettlementDraftDto`, `UpdateSettlementDto`,
/// `AddManualLineDto`, `WaiveLineDto`, `FinalizeSettlementDto` and
/// `CancelSettlementDto`, field for field.

export const openSettlementDraftBodySchema = z
  .object({
    membershipId: z.string(),
    relievingDate: z.string(),
    exitReason: z.enum(SETTLEMENT_EXIT_REASONS),
  })
  .strict();

export type OpenSettlementDraftBody = z.infer<typeof openSettlementDraftBodySchema>;

export const updateSettlementBodySchema = z
  .object({
    version: z.number(),
    settlementMode: z.enum(SETTLEMENT_MODES).optional(),
    relievingDate: z.string().optional(),
    settlementDate: z.string().optional(),
    personalEmail: z.string().optional(),
    payeeOverride: payeeOverrideWriteSchema.optional(),
  })
  .strict();

export type UpdateSettlementBody = z.infer<typeof updateSettlementBodySchema>;

export const addManualLineBodySchema = z
  .object({
    side: z.enum(FNF_LINE_SIDES),
    kind: z.enum(FNF_LINE_KINDS),
    description: z.string(),
    amount: z.string(),
    currency: z.string(),
  })
  .strict();

export type AddManualLineBody = z.infer<typeof addManualLineBodySchema>;

export const waiveLineBodySchema = z
  .object({
    version: z.number(),
    waivedAmount: z.string(),
    waiveReason: z.string(),
  })
  .strict();

export type WaiveLineBody = z.infer<typeof waiveLineBodySchema>;

export const cancelSettlementBodySchema = z
  .object({ version: z.number(), reason: z.string() })
  .strict();

export type CancelSettlementBody = z.infer<typeof cancelSettlementBodySchema>;

/// The own-scope shapes (`ownSettlementSummarySchema`,
/// `ownSettlementsPageSchema`, `listOwnSettlementsQuerySchema`) and the
/// statement response and dispute body now live in
/// `@/shared/pay-records/pay-records.schema`: the alumni landing reads the
/// same endpoints and a feature may not import a feature (A6).
